/**
 * SavDetail — Page dédiée d'un dossier SAV (agence).
 *
 * Réutilise le langage visuel Wyvio (hero, timeline, tabs, cards).
 * Aucun langage neuf : c'est un dossier véhicule, chapitre SAV.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, LifeBuoy, CheckCircle2, CircleDashed, Clock, User, Send,
  Loader2, X, MessagesSquare, Activity, ChevronRight, AlertTriangle,
  ListChecks, Copy, PlayCircle,
} from "lucide-react";
import api from "@/lib/api";
import { formatFrDate, formatTime } from "@/lib/format";
import { toast } from "sonner";

const STATUS_META = {
  open:                    { label: "Ouvert",               bg: "bg-[#DBEAFE]", fg: "text-[#1E40AF]" },
  analysis_in_progress:    { label: "Analyse en cours",     bg: "bg-[#DBEAFE]", fg: "text-[#1E40AF]" },
  solution_in_progress:    { label: "Solution en cours",    bg: "bg-[#FEF3C7]", fg: "text-[#92400E]" },
  appointment_scheduled:   { label: "Rendez-vous programmé", bg: "bg-[#EDE9FE]", fg: "text-[#5B21B6]" },
  resolved:                { label: "Résolu",               bg: "bg-[#DCFCE7]", fg: "text-[#166534]" },
  closed:                  { label: "Clôturé",              bg: "bg-[#EEF2F5]", fg: "text-[#6B7280]" },
};

const STATUS_ORDER = [
  "open", "analysis_in_progress", "solution_in_progress",
  "appointment_scheduled", "resolved", "closed",
];

const SavDetail = () => {
  const { id } = useParams();
  const [sav, setSav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("workflow");
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [resolutionSummary, setResolutionSummary] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);
  const [timeline, setTimeline] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.getSav(id),
        api.getSavTimeline(id, { limit: 100 }).catch(() => ({ events: [] })),
      ]);
      setSav(s);
      setTimeline(t.events || []);
      setError(null);
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const readOnly = sav?.status === "closed";

  const handleSend = async () => {
    if (!msgText.trim() || sending) return;
    setSending(true);
    try {
      await api.sendSavMessage(id, msgText.trim());
      setMsgText("");
      await load();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const handleStatus = async (newStatus) => {
    if (statusBusy || readOnly) return;
    setStatusBusy(true);
    try {
      await api.patchSav(id, { status: newStatus });
      await load();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setStatusBusy(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await api.closeSav(id, resolutionSummary || null);
      setShowClose(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setClosing(false);
    }
  };

  const handleUpdateStep = async (stepKey, newState) => {
    if (readOnly) return;
    try {
      await api.updateSavStep(id, stepKey, { state: newState });
      await load();
    } catch (e) {
      toast?.error?.(e.message || "Erreur");
    }
  };

  const tone = useMemo(() => STATUS_META[sav?.status] || STATUS_META.open, [sav?.status]);

  if (loading) {
    return (
      <div className="wyvio-fade-in" data-testid="sav-detail-loading">
        <div className="wyvio-card p-10 animate-pulse space-y-4">
          <div className="h-4 w-32 bg-[#EEF2F5] rounded" />
          <div className="h-8 w-64 bg-[#EEF2F5] rounded" />
        </div>
      </div>
    );
  }

  if (error || !sav) {
    return (
      <div className="wyvio-card p-8" data-testid="sav-detail-error">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center">
            <AlertTriangle size={17} strokeWidth={2} />
          </div>
          <div>
            <p className="text-[14px] font-semibold">Dossier SAV introuvable</p>
            <p className="text-[12.5px] text-[#6B7280]">{error || "Ce dossier n'existe pas."}</p>
          </div>
        </div>
        <Link to="/app" className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium">
          <ArrowLeft size={14} /> Retour
        </Link>
      </div>
    );
  }

  return (
    <div className="wyvio-fade-in" data-testid="page-sav-detail">
      {/* Fil d'ariane vers dossier maître */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <Link
          to={sav.dossier_summary ? `/app/dossier/${sav.dossier_summary.id}` : "/app"}
          data-testid="back-to-dossier"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6B7280] hover:text-[#111]"
        >
          <ArrowLeft size={15} strokeWidth={2} />
          {sav.dossier_summary
            ? `Retour · ${sav.dossier_summary.order_number}`
            : "Retour"}
        </Link>
        {!readOnly && (
          <button
            type="button"
            data-testid="sav-close-btn"
            onClick={() => setShowClose(true)}
            className="h-9 inline-flex items-center gap-1.5 rounded-full bg-white border border-[#EEF2F5] px-3.5 text-[12.5px] font-medium text-[#111] hover:bg-[#F7F8FA]"
          >
            <CheckCircle2 size={13} strokeWidth={2} />
            Clôturer
          </button>
        )}
      </div>

      {/* Hero SAV */}
      <div className="wyvio-card p-6 lg:p-7">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-full bg-[#111] text-[#C8FF2F] flex items-center justify-center shrink-0">
            <LifeBuoy size={19} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[#6B7280]">
                Dossier SAV
              </span>
              <span className="text-[13px] font-mono text-[#111]" data-testid="sav-reference">
                {sav.reference}
              </span>
              <span className={`text-[10.5px] font-medium ${tone.fg} ${tone.bg} rounded-full px-2 py-0.5`}>
                {tone.label}
              </span>
            </div>
            <h1 className="mt-1 text-[20px] font-semibold text-[#111] tracking-tight break-words">
              {sav.title}
            </h1>
            <p className="mt-1 text-[13px] text-[#6B7280]">
              {sav.vehicle?.vehicle_name} · {sav.vehicle?.order_number}
            </p>
            <div className="mt-3 flex items-center gap-4 text-[11.5px] text-[#6B7280]">
              <span className="inline-flex items-center gap-1.5">
                <User size={11} strokeWidth={2} />
                {sav.responsible?.name || "Non assigné"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={11} strokeWidth={2} />
                Ouvert le {formatFrDate(sav.opened_at)}
              </span>
              {sav.closed_at && (
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={11} strokeWidth={2} />
                  Clôturé le {formatFrDate(sav.closed_at)}
                </span>
              )}
            </div>
          </div>
        </div>
        <p className="mt-4 text-[13px] text-[#374151] whitespace-pre-wrap">
          {sav.short_description}
        </p>
        {sav.resolution_summary && (
          <div className="mt-4 rounded-2xl bg-[#F7F8FA] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">
              Résolution communiquée au client
            </p>
            <p className="mt-1 text-[13px] text-[#111] whitespace-pre-wrap">
              {sav.resolution_summary}
            </p>
          </div>
        )}
      </div>

      {/* Statut · Progression rapide */}
      {!readOnly && (
        <div className="mt-5 wyvio-card p-5" data-testid="sav-status-picker">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280] mb-3">
            Faire évoluer le statut
          </p>
          <div className="flex items-center flex-wrap gap-1.5">
            {STATUS_ORDER.filter((s) => s !== "closed").map((s) => {
              const st = STATUS_META[s];
              const active = sav.status === s;
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`sav-status-${s}`}
                  disabled={statusBusy || active}
                  onClick={() => handleStatus(s)}
                  className={[
                    "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11.5px] font-medium transition",
                    active
                      ? "bg-[#111] text-white cursor-default"
                      : "bg-white border border-[#EEF2F5] text-[#6B7280] hover:text-[#111]",
                  ].join(" ")}
                >
                  {st.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-1 p-1 bg-white border border-[#EEF2F5] rounded-full w-fit">
        <button
          type="button"
          data-testid="sav-tab-workflow"
          onClick={() => setTab("workflow")}
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-medium transition ${tab === "workflow" ? "bg-[#111] text-white" : "text-[#6B7280] hover:text-[#111]"}`}
        >
          <ListChecks size={13} strokeWidth={2} /> Workflow
        </button>
        <button
          type="button"
          data-testid="sav-tab-timeline"
          onClick={() => setTab("timeline")}
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-medium transition ${tab === "timeline" ? "bg-[#111] text-white" : "text-[#6B7280] hover:text-[#111]"}`}
        >
          <Activity size={13} strokeWidth={2} /> Timeline
        </button>
        <button
          type="button"
          data-testid="sav-tab-messages"
          onClick={() => setTab("messages")}
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-medium transition ${tab === "messages" ? "bg-[#111] text-white" : "text-[#6B7280] hover:text-[#111]"}`}
        >
          <MessagesSquare size={13} strokeWidth={2} />
          Messages
          {sav.messages?.length > 0 && (
            <span className={`min-w-[16px] h-4 rounded-full text-[10px] font-semibold flex items-center justify-center px-1 ${tab === "messages" ? "bg-[#C8FF2F] text-[#111]" : "bg-[#111] text-white"}`}>
              {sav.messages.length}
            </span>
          )}
        </button>
      </div>

      {tab === "workflow" && (
        <div className="mt-5 wyvio-card p-6" data-testid="sav-workflow-panel">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280] mb-4">
            Étapes du dossier SAV
          </p>
          <ol className="relative space-y-1">
            <span aria-hidden className="absolute left-[19px] top-4 bottom-4 w-px bg-[#EEF2F5]" />
            {(sav.workflow || []).map((step) => {
              const done = step.state === "completed";
              const active = step.state === "in_progress" || step.state === "waiting";
              return (
                <li
                  key={step.key}
                  data-testid={`sav-step-${step.key}`}
                  className="relative flex items-start gap-4 py-2.5 rounded-2xl px-2 -mx-2 group hover:bg-[#FAFBFC] transition"
                >
                  <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-[#DCFCE7] text-[#166534]" : active ? "bg-[#C8FF2F] text-[#111]" : "bg-[#EEF2F5] text-[#9CA3AF]"}`}>
                    {done ? (
                      <CheckCircle2 size={16} strokeWidth={2} />
                    ) : active ? (
                      <PlayCircle size={16} strokeWidth={2} />
                    ) : (
                      <CircleDashed size={16} strokeWidth={2} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-[13.5px] ${done || active ? "font-semibold text-[#111]" : "text-[#6B7280]"}`}>
                        {step.title}
                      </p>
                      {step.visible_client && (
                        <span className="text-[9.5px] font-medium uppercase tracking-wider text-[#166534] bg-[#DCFCE7] rounded-full px-1.5 py-0.5">
                          visible client
                        </span>
                      )}
                    </div>
                    {step.description && (
                      <p className="mt-0.5 text-[11.5px] text-[#6B7280]">
                        {step.description}
                      </p>
                    )}
                    {step.client_message && (done || active) && (
                      <p className="mt-1.5 text-[11px] text-[#6B7280] italic">
                        Message client : « {step.client_message} »
                      </p>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="opacity-0 group-hover:opacity-100 transition flex items-center gap-1 shrink-0">
                      {step.state !== "in_progress" && (
                        <button
                          type="button"
                          data-testid={`sav-step-${step.key}-start`}
                          onClick={() => handleUpdateStep(step.key, "in_progress")}
                          className="h-7 px-2.5 rounded-full text-[10.5px] font-medium bg-white border border-[#EEF2F5] text-[#6B7280] hover:text-[#111]"
                        >
                          Démarrer
                        </button>
                      )}
                      {step.state !== "completed" && (
                        <button
                          type="button"
                          data-testid={`sav-step-${step.key}-complete`}
                          onClick={() => handleUpdateStep(step.key, "completed")}
                          className="h-7 px-2.5 rounded-full text-[10.5px] font-semibold bg-[#C8FF2F] text-[#111] hover:brightness-95"
                        >
                          Terminer
                        </button>
                      )}
                      {step.state !== "not_started" && (
                        <button
                          type="button"
                          data-testid={`sav-step-${step.key}-reset`}
                          onClick={() => handleUpdateStep(step.key, "not_started")}
                          className="h-7 px-2.5 rounded-full text-[10.5px] font-medium text-[#9CA3AF] hover:text-[#111]"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {tab === "timeline" && (
        <div className="mt-5 wyvio-card p-6" data-testid="sav-timeline">
          {timeline.length === 0 ? (
            <p className="text-center text-[12.5px] text-[#9CA3AF] py-8">
              Aucun événement pour l&apos;instant.
            </p>
          ) : (
            <ol className="relative space-y-3">
              <span aria-hidden className="absolute left-[15px] top-2 bottom-2 w-px bg-[#EEF2F5]" />
              {timeline.map((e) => (
                <li key={e.id} className="relative flex gap-4 py-1.5" data-testid={`sav-event-${e.type}`}>
                  <div className="h-8 w-8 rounded-full bg-[#EEF2F5] text-[#6B7280] flex items-center justify-center shrink-0 relative z-10">
                    <ChevronRight size={12} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-[#111]">
                      {(e.meta?.step_label || e.meta?.preview || e.type)}
                      {e.meta?.from && e.meta?.to && (
                        <span className="text-[#9CA3AF]"> · {e.meta.from} → {e.meta.to}</span>
                      )}
                    </p>
                    <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
                      {formatTime(e.at)} · {e.actor?.name || "Système"}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {tab === "messages" && (
        <div className="mt-5 wyvio-card p-5" data-testid="sav-messages">
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {(sav.messages || []).length === 0 ? (
              <p className="text-center text-[12.5px] text-[#9CA3AF] py-6">
                Aucun message échangé.
              </p>
            ) : (
              sav.messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`sav-msg-${m.from_side}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] ${m.from_side === "agency" ? "ml-auto bg-[#111] text-white" : "bg-[#F7F8FA] text-[#111]"}`}
                >
                  <p className="whitespace-pre-wrap">{m.text}</p>
                  <p className={`mt-1 text-[10px] ${m.from_side === "agency" ? "text-white/60" : "text-[#9CA3AF]"}`}>
                    {m.author} · {formatTime(m.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>
          {!readOnly && (
            <div className="mt-4 flex items-end gap-2">
              <textarea
                data-testid="sav-msg-input"
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                rows={2}
                placeholder="Écrire un message au client…"
                className="flex-1 rounded-2xl border border-[#EEF2F5] bg-white px-3 py-2.5 text-[13px] text-[#111] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#C8FF2F]/60 resize-none"
              />
              <button
                type="button"
                data-testid="sav-msg-send"
                onClick={handleSend}
                disabled={!msgText.trim() || sending}
                className="h-10 px-4 inline-flex items-center gap-1.5 rounded-full bg-[#111] text-white text-[12.5px] font-medium hover:bg-black transition disabled:opacity-50"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} strokeWidth={2} />}
                Envoyer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal de clôture */}
      {showClose && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setShowClose(false)}
        >
          <div
            className="wyvio-card w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="sav-close-modal"
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-[15.5px] font-semibold">Clôturer ce dossier SAV ?</h3>
              <button onClick={() => setShowClose(false)} className="h-8 w-8 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center">
                <X size={14} />
              </button>
            </div>
            <p className="text-[12.5px] text-[#6B7280]">
              Le client sera notifié. L&apos;historique reste consultable en lecture seule.
              Vous pouvez ajouter un résumé de résolution (optionnel).
            </p>
            <textarea
              data-testid="sav-close-summary"
              value={resolutionSummary}
              onChange={(e) => setResolutionSummary(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Résumé partagé au client (optionnel)…"
              className="mt-3 w-full rounded-2xl border border-[#EEF2F5] p-3 text-[13px] text-[#111] focus:outline-none focus:ring-2 focus:ring-[#C8FF2F]/60 resize-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowClose(false)}
                className="h-10 px-4 rounded-full text-[12.5px] font-medium text-[#6B7280] hover:text-[#111]"
              >
                Annuler
              </button>
              <button
                type="button"
                data-testid="sav-close-confirm"
                onClick={handleClose}
                disabled={closing}
                className="h-10 px-5 rounded-full inline-flex items-center gap-1.5 bg-[#C8FF2F] text-[#111] text-[12.5px] font-semibold hover:brightness-95 transition disabled:opacity-50"
              >
                {closing && <Loader2 size={13} className="animate-spin" />}
                Confirmer la clôture
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavDetail;
